import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  applyCodePatch,
  analyzeCodexPlanningDocument,
  analyzeCodexReferenceImage,
  analyzeCodexSourceZip,
  addKnowledgeFile,
  addKnowledgeText,
  approveAgentTask,
  createCodexAppBlueprint,
  createCodexTaskPlan,
  createCodePatch,
  createCodeProposal,
  createAdminUser,
  decideCodeProposal,
  deleteAdminUser,
  deleteCodePatch,
  deleteCodeProposal,
  dryRunCodePatch,
  deleteKnowledgeItem,
  fetchAdminDashboard,
  fetchCodexProjectMap,
  fetchDevConsoleRoots,
  fetchOpsServers,
  fetchOpsHealthHistory,
  fetchOpsServiceMap,
  fetchKnowledgeItems,
  fetchLearnedPending,
  setLearnedStatus,
  fetchServerStatus,
  getSearchSettings,
  getWeatherSettings,
  prepareCodePatch,
  readDevConsoleFile,
  regenerateCodePatch,
  restartBackend,
  restoreCodePatch,
  listDevConsoleDir,
  runDevConsoleCommand,
  runAgentDocumentTask,
  runDeployCheck,
  runFrontendBuild,
  runOpsExec,
  runOpsHealth,
  runOpsInventory,
  runSafeDeploy,
  runServerAction,
  saveOpsServer,
  testSearchSettings,
  testWeatherSettings,
  resetAdminUserPassword,
  updateAdminUser,
  updateSearchSettings,
  updateWeatherSettings,
  uploadDeployZip,
  validateDeployZip,
  verifyAdminUserContact,
  verifyCodePatch,
  writeDevConsoleFile,
} from "./api/adminApi";
import AdminSummary from "./admin/AdminSummary";
import DashboardCards from "./admin/DashboardCards";
import DashboardPanels from "./admin/DashboardPanels";
import DeploymentPanel from "./admin/DeploymentPanel";
import KnowledgeBasePanel from "./admin/KnowledgeBasePanel";
import LearnedReviewPanel from "./admin/LearnedReviewPanel";
import SearchSettingsPanel from "./admin/SearchSettingsPanel";
import ServerManagementPanel from "./admin/ServerManagementPanel";
import DevConsolePanel from "./admin/DevConsolePanel";
import WeatherSettingsPanel from "./admin/WeatherSettingsPanel";
import DocumentTaskPanel from "./admin/DocumentTaskPanel";
import EnterpriseClientSyncPanel from "./admin/EnterpriseClientSyncPanel";
import UserManagementTable from "./admin/UserManagementTable";
import TenantOnboardingPanel from "./admin/TenantOnboardingPanel";
import ClientSyncPanel from "./admin/ClientSyncPanel";
import ExternalApiPanel from "./admin/ExternalApiPanel";
import { ROLE_OPTIONS, visibleRoleOptions } from "./utils/roles";

function AdminDashboard({ apiBase, token, onPendingCountChange, currentUser }) {
  const [users, setUsers] = useState([]);
  const [overview, setOverview] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [agentTasks, setAgentTasks] = useState([]);
  const [agentStatus, setAgentStatus] = useState("unknown");
  const [agentError, setAgentError] = useState("");
  const [codeProposals, setCodeProposals] = useState([]);
  const [codePatches, setCodePatches] = useState([]);
  const [codexProjectMap, setCodexProjectMap] = useState(null);
  const [codexTaskPlan, setCodexTaskPlan] = useState(null);
  const [codexAppBlueprint, setCodexAppBlueprint] = useState(null);
  const [codexSourceMap, setCodexSourceMap] = useState(null);
  const [codexImageBrief, setCodexImageBrief] = useState(null);
  const [codexPlanningBrief, setCodexPlanningBrief] = useState(null);
  const [deployRuns, setDeployRuns] = useState([]);
  const [serverStatus, setServerStatus] = useState(null);
  const [devConsoleRoots, setDevConsoleRoots] = useState([]);
  const [devConsoleCwd, setDevConsoleCwd] = useState("/home/tobe-project");
  const [devConsoleParent, setDevConsoleParent] = useState("");
  const [devConsoleItems, setDevConsoleItems] = useState([]);
  const [devConsoleFile, setDevConsoleFile] = useState(null);
  const [devConsoleContent, setDevConsoleContent] = useState("");
  const [devConsoleCommand, setDevConsoleCommand] = useState("");
  const [devConsoleOutput, setDevConsoleOutput] = useState("");
  const [devConsoleBusy, setDevConsoleBusy] = useState(false);
  const [devConsoleNotice, setDevConsoleNotice] = useState("");
  const [searchSettings, setSearchSettings] = useState(null);
  const [weatherSettings, setWeatherSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [adminPage, setAdminPage] = useState("overview");
  const [approvingTaskIds, setApprovingTaskIds] = useState([]);
  const [runningDocumentTask, setRunningDocumentTask] = useState(false);
  const [creatingProposal, setCreatingProposal] = useState(false);
  const [creatingBlueprint, setCreatingBlueprint] = useState(false);
  const [analyzingSourceZip, setAnalyzingSourceZip] = useState(false);
  const [analyzingReferenceImage, setAnalyzingReferenceImage] = useState(false);
  const [analyzingPlanningDocument, setAnalyzingPlanningDocument] = useState(false);
  const [patchingProposalId, setPatchingProposalId] = useState(null);
  const [preparingPatchId, setPreparingPatchId] = useState(null);
  const [dryRunningPatchId, setDryRunningPatchId] = useState(null);
  const [applyingPatchId, setApplyingPatchId] = useState(null);
  const [verifyingPatchId, setVerifyingPatchId] = useState(null);
  const [restoringPatchId, setRestoringPatchId] = useState(null);
  const [adminNotice, setAdminNotice] = useState("");
  const [adminDialog, setAdminDialog] = useState(null);
  const adminDialogResolverRef = useRef(null);

  const isSuperAdmin = currentUser?.role === "admin";
  const canCreateCompanyUsers = ["admin", "company_owner"].includes(currentUser?.role);
  const visibleRoles = useMemo(() => visibleRoleOptions(isSuperAdmin, currentUser?.role), [isSuperAdmin, currentUser?.role]);
  const showAdminNotice = (message) => {
    setAdminNotice(message || "");
    if (message) window.setTimeout(() => setAdminNotice(""), 4600);
  };

  const closeAdminDialog = (value) => {
    if (adminDialogResolverRef.current) {
      adminDialogResolverRef.current(value);
      adminDialogResolverRef.current = null;
    }
    setAdminDialog(null);
  };

  const requestAdminDialog = (config) => new Promise((resolve) => {
    adminDialogResolverRef.current = resolve;
    setAdminDialog({ inputValue: config.defaultValue || "", ...config });
  });

  const requestAdminConfirm = async (message, options = {}) => Boolean(await requestAdminDialog({ message, ...options }));

  const requestAdminText = async (message, defaultValue = "") => {
    const value = await requestAdminDialog({ message, defaultValue, inputMode: "text", confirmLabel: "확인" });
    return typeof value === "string" ? value : null;
  };

  const confirmAdminDialog = () => {
    const value = adminDialog?.inputValue || "";
    if (adminDialog?.expectedText && value !== adminDialog.expectedText) {
      showAdminNotice(`${adminDialog.expectedText}를 정확히 입력해주세요.`);
      return;
    }
    closeAdminDialog(adminDialog?.inputMode === "text" ? value : true);
  };

  const fetchSearchSettings = async () => {
    try {
      const data = await getSearchSettings(apiBase, token);
      setSearchSettings(data.settings);
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchWeatherSettings = async () => {
    // ★2026-09-18: 날씨 설정은 본사 최고관리자 전용(백엔드 ensure_platform_admin). 협력사 관리자는
    //   로그인만 해도 403 → "날씨 설정을 불러오지 못했습니다" 빨간 배너가 떴다. 아예 부르지 않는다.
    if (!isSuperAdmin) return;
    try {
      const data = await getWeatherSettings(apiBase, token);
      setWeatherSettings(data.settings);
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchDashboard = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchAdminDashboard(apiBase, token);
      setOverview(data.overview);
      setUsers(data.users);
      setAuditLogs(data.auditLogs);
      setAgentTasks(data.agentTasks);
      setAgentStatus(data.agentStatus);
      setAgentError(data.agentError || "");
      setCodeProposals(data.codeProposals || []);
      setCodePatches(data.codePatches || []);
      setDeployRuns(data.deployRuns || []);
      const mapData = await fetchCodexProjectMap(apiBase, token).catch(() => null);
      setCodexProjectMap(mapData?.project_map || null);
      const serverData = await fetchServerStatus(apiBase, token).catch(() => null);
      setServerStatus(serverData);
      await fetchSearchSettings();
      await fetchWeatherSettings();
      onPendingCountChange?.(data.users.filter((user) => !user.is_approved).length);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateUser = async (targetUserid, payload, successMessage, options = {}) => {
    try {
      await updateAdminUser(apiBase, token, targetUserid, payload);
      if (successMessage && !options.silent) showAdminNotice(successMessage);
      await fetchDashboard();
      return true;
    } catch (err) {
      showAdminNotice(err.message);
      return false;
    }
  };

  const handleApprove = async (targetUserid, options = {}) => {
    if (!options.silent && !(await requestAdminConfirm(`${targetUserid} 계정을 승인하시겠습니까?`))) return false;
    return updateUser(targetUserid, { is_approved: true }, "승인되었습니다.", options);
  };

  const handleCreateUser = async (payload) => {
    try {
      await createAdminUser(apiBase, token, payload);
      showAdminNotice("직원 계정이 생성되었습니다.");
      await fetchDashboard();
      return true;
    } catch (err) {
      showAdminNotice(err.message);
      return false;
    }
  };

  const handleRoleChange = async (targetUserid, role) => {
    const roleName = ROLE_OPTIONS.find((item) => item.id === role)?.label || role;
    if (!(await requestAdminConfirm(`${targetUserid} 권한을 '${roleName}'(으)로 변경하시겠습니까?`))) {
      fetchDashboard();
      return;
    }
    return updateUser(targetUserid, { role }, "권한이 변경되었습니다.");
  };

  const handleCompanyChange = async (targetUserid, company) => {
    const nextCompany = await requestAdminText("회사명", company || "");
    if (!nextCompany || nextCompany === company) return;
    return updateUser(targetUserid, { company: nextCompany }, "회사명이 변경되었습니다.");
  };

  const handleContactChange = async (targetUserid, user) => {
    const name = await requestAdminText("이름", user?.name || "");
    if (name === null) return;
    const email = await requestAdminText("이메일", user?.email || "");
    if (email === null) return;
    const phone = await requestAdminText("전화번호", user?.phone || "");
    if (phone === null) return;
    return updateUser(targetUserid, { name, email, phone }, "회원 정보가 수정되었습니다.");
  };

  const handlePasswordReset = async (targetUserid) => {
    const newPassword = await requestAdminText(`${targetUserid} 임시 비밀번호를 입력하세요. 8자 이상`);
    if (!newPassword) return;
    if (newPassword.length < 8) {
      showAdminNotice("임시 비밀번호는 8자 이상이어야 합니다.");
      return;
    }
    if (!(await requestAdminConfirm(`${targetUserid} 계정의 비밀번호를 임시 비밀번호로 변경하시겠습니까?`))) return;
    try {
      await resetAdminUserPassword(apiBase, token, targetUserid, newPassword);
      showAdminNotice("임시 비밀번호로 변경되었습니다.");
      await fetchDashboard();
    } catch (err) {
      showAdminNotice(err.message);
    }
  };

  const handleVerifyContact = async (targetUserid, user) => {
    if (!user?.email || !user?.phone) {
      showAdminNotice("이메일과 전화번호가 모두 등록되어야 인증 처리할 수 있습니다.");
      return;
    }
    if (!(await requestAdminConfirm(`${targetUserid} 계정의 이메일/전화번호를 인증 확인 처리하시겠습니까?`))) return;
    try {
      await verifyAdminUserContact(apiBase, token, targetUserid, {
        email_verified: true,
        phone_verified: true,
      });
      showAdminNotice("연락처 인증 상태가 저장되었습니다.");
      await fetchDashboard();
    } catch (err) {
      showAdminNotice(err.message);
    }
  };

  const handleDeleteUser = async (targetUserid) => {
    if (!(await requestAdminConfirm(`${targetUserid} 계정을 탈퇴 처리하시겠습니까? 이 계정은 로그인할 수 없게 됩니다.`, { expectedText: "DELETE", inputLabel: "계속하려면 DELETE를 입력하세요." }))) return;
    try {
      await deleteAdminUser(apiBase, token, targetUserid);
      showAdminNotice("회원 탈퇴 처리가 완료되었습니다.");
      await fetchDashboard();
    } catch (err) {
      showAdminNotice(err.message);
    }
  };

  const handleAgentApprove = async (taskId, approve) => {
    const message = approve ? "이 Agent 패키지 설치/작업 실행을 승인하시겠습니까?" : "이 Agent 작업을 거절하시겠습니까?";
    if (!(await requestAdminConfirm(message))) return;
    setApprovingTaskIds((prev) => [...new Set([...prev, taskId])]);
    try {
      await approveAgentTask(apiBase, token, taskId, approve);
      await fetchDashboard();
    } catch (err) {
      showAdminNotice(err.message);
    } finally {
      setApprovingTaskIds((prev) => prev.filter((id) => id !== taskId));
    }
  };

  const handleDocumentTaskRun = async (file, instruction) => {
    if (!file && !instruction.trim()) {
      showAdminNotice("Add a work document or instruction.");
      return;
    }
    if (!(await requestAdminConfirm("Start Agent work from this document? Package installs will stop for approval."))) return;
    setRunningDocumentTask(true);
    try {
      await runAgentDocumentTask(apiBase, token, file, instruction);
      await fetchDashboard();
    showAdminNotice("문서 기반 작업을 요청했습니다. 결과나 승인 요청은 Agent 작업 메뉴에서 확인하세요.");
    } catch (err) {
      showAdminNotice(err.message);
    } finally {
      setRunningDocumentTask(false);
    }
  };

  const handleCreateProposal = async (file, instruction) => {
    if (!file && !instruction.trim()) {
      showAdminNotice("Add a work document or instruction.");
      return;
    }
    setCreatingProposal(true);
    try {
      await createCodeProposal(apiBase, token, file, instruction);
      await fetchDashboard();
      showAdminNotice("코드 수정 제안서를 만들었습니다. 운영 파일은 아직 변경되지 않았습니다.");
    } catch (err) {
      showAdminNotice(err.message);
    } finally {
      setCreatingProposal(false);
    }
  };

  const handleCreateCodexTaskPlan = async (instruction) => {
    if (!instruction.trim()) {
      showAdminNotice("Codex 작업 지시를 입력해주세요.");
      return;
    }
    try {
      const data = await createCodexTaskPlan(apiBase, token, instruction);
      setCodexTaskPlan(data.task_plan);
      showAdminNotice(data.task_plan?.summary || "Codex 작업 계획을 생성했습니다.");
    } catch (err) {
      showAdminNotice(err.message);
    }
  };

  const handleCreateCodexAppBlueprint = async (conversation) => {
    if (!conversation.trim()) {
      showAdminNotice("만들고 싶은 앱이나 홈페이지를 대화하듯 입력해주세요.");
      return;
    }
    setCreatingBlueprint(true);
    try {
      const data = await createCodexAppBlueprint(apiBase, token, conversation, "TOBE App");
      setCodexAppBlueprint(data.blueprint);
      setCodexTaskPlan(null);
      showAdminNotice(data.blueprint?.summary || "앱 제작 블루프린트를 생성했습니다.");
    } catch (err) {
      showAdminNotice(err.message);
    } finally {
      setCreatingBlueprint(false);
    }
  };

  const handleAnalyzeCodexSourceZip = async (file) => {
    if (!file) {
      showAdminNotice("분석할 소스 ZIP 파일을 선택해주세요.");
      return;
    }
    setAnalyzingSourceZip(true);
    try {
      const data = await analyzeCodexSourceZip(apiBase, token, file);
      setCodexSourceMap(data.source_map);
      showAdminNotice(data.source_map?.summary || "소스 ZIP 분석을 완료했습니다.");
    } catch (err) {
      showAdminNotice(err.message);
    } finally {
      setAnalyzingSourceZip(false);
    }
  };

  const handleAnalyzeCodexReferenceImage = async (file, instruction = "") => {
    if (!file) {
      showAdminNotice("분석할 참조 이미지를 선택해주세요.");
      return;
    }
    setAnalyzingReferenceImage(true);
    try {
      const data = await analyzeCodexReferenceImage(apiBase, token, file, instruction);
      setCodexImageBrief(data.image_brief);
      showAdminNotice(data.image_brief?.summary || "참조 이미지 분석을 완료했습니다.");
    } catch (err) {
      showAdminNotice(err.message);
    } finally {
      setAnalyzingReferenceImage(false);
    }
  };

  const handleAnalyzeCodexPlanningDocument = async (file, instruction = "") => {
    if (!file && !instruction.trim()) {
      showAdminNotice("기획서 파일을 선택하거나 기획 내용을 입력해주세요.");
      return;
    }
    setAnalyzingPlanningDocument(true);
    try {
      const data = await analyzeCodexPlanningDocument(apiBase, token, file, instruction);
      setCodexPlanningBrief(data.planning_brief);
      setCodexTaskPlan(null);
      showAdminNotice(data.planning_brief?.summary || "기획서 분석을 완료했습니다.");
    } catch (err) {
      showAdminNotice(err.message);
    } finally {
      setAnalyzingPlanningDocument(false);
    }
  };

  const handleDecideProposal = async (proposalId, approve) => {
    const message = approve ? "이 코드 수정 제안서를 승인하시겠습니까?" : "이 코드 수정 제안서를 거절하시겠습니까?";
    if (!(await requestAdminConfirm(message))) return;
    try {
      await decideCodeProposal(apiBase, token, proposalId, approve);
      await fetchDashboard();
    } catch (err) {
      showAdminNotice(err.message);
    }
  };

  const handleDeleteProposal = async (proposalId) => {
    if (!(await requestAdminConfirm("Delete this reviewed code proposal and its patch drafts? Applied patches must be restored first.", { expectedText: "DELETE", inputLabel: "Type DELETE to continue." }))) return;
    try {
      await deleteCodeProposal(apiBase, token, proposalId);
      await fetchDashboard();
      showAdminNotice("Code proposal was deleted.");
    } catch (err) {
      showAdminNotice(err.message);
      await fetchDashboard();
    }
  };

  const handleCreatePatch = async (proposalId) => {
    if (!(await requestAdminConfirm("승인된 제안서로 패치 초안을 생성하시겠습니까?"))) return;
    setPatchingProposalId(proposalId);
    try {
      await createCodePatch(apiBase, token, proposalId);
      await fetchDashboard();
      showAdminNotice("Patch draft created. Review it before preparing backup.");
    } catch (err) {
      showAdminNotice(err.message);
    } finally {
      setPatchingProposalId(null);
    }
  };

  const handleDeletePatch = async (patchId) => {
    if (!(await requestAdminConfirm("Delete this patch draft? Applied patches must be restored first.", { expectedText: "DELETE", inputLabel: "Type DELETE to continue." }))) return;
    try {
      await deleteCodePatch(apiBase, token, patchId);
      await fetchDashboard();
      showAdminNotice("Patch draft was deleted.");
    } catch (err) {
      showAdminNotice(err.message);
      await fetchDashboard();
    }
  };

  const handleDryRunPatch = async (patchId) => {
    setDryRunningPatchId(patchId);
    try {
      const data = await dryRunCodePatch(apiBase, token, patchId);
      await fetchDashboard();
      showAdminNotice(data.result?.summary || "Dry-run passed. No operating file was changed.");
    } catch (err) {
      showAdminNotice(err.message);
      await fetchDashboard();
    } finally {
      setDryRunningPatchId(null);
    }
  };

  const handleRegeneratePatch = async (patchId) => {
    if (!(await requestAdminConfirm("Regenerate this patch draft from the approved proposal? Existing patch file and backup references will be cleared."))) return;
    setPatchingProposalId(`patch-${patchId}`);
    try {
      await regenerateCodePatch(apiBase, token, patchId);
      await fetchDashboard();
      showAdminNotice("Patch draft was regenerated. Run dry-run before preparing backup.");
    } catch (err) {
      showAdminNotice(err.message);
      await fetchDashboard();
    } finally {
      setPatchingProposalId(null);
    }
  };

  const handlePreparePatch = async (patchId) => {
    if (!(await requestAdminConfirm("패치 파일과 백업 ZIP을 준비하시겠습니까? 아직 운영 파일은 변경되지 않습니다."))) return;
    setPreparingPatchId(patchId);
    try {
      await prepareCodePatch(apiBase, token, patchId);
      await fetchDashboard();
      showAdminNotice("패치 적용 준비가 완료되었습니다. 패치 파일과 백업 ZIP을 확인하세요.");
    } catch (err) {
      showAdminNotice(err.message);
    } finally {
      setPreparingPatchId(null);
    }
  };

  const handleApplyPatch = async (patchId) => {
    if (!(await requestAdminConfirm("최종 적용은 운영 파일을 변경합니다. 백업 ZIP과 패치 파일을 확인하셨습니까?", { expectedText: "APPLY", inputLabel: "Type APPLY to continue." }))) return;
    setApplyingPatchId(patchId);
    try {
      await applyCodePatch(apiBase, token, patchId);
      await fetchDashboard();
      showAdminNotice("패치를 적용했습니다. 검증을 실행한 뒤 배포 관리에서 반영하세요.");
    } catch (err) {
      showAdminNotice(err.message);
      await fetchDashboard();
    } finally {
      setApplyingPatchId(null);
    }
  };

  const handleVerifyPatch = async (patchId) => {
    if (!(await requestAdminConfirm("Run verification after apply?"))) return;
    setVerifyingPatchId(patchId);
    try {
      await verifyCodePatch(apiBase, token, patchId);
      await fetchDashboard();
      showAdminNotice("Verification completed. Check validation log.");
    } catch (err) {
      showAdminNotice(err.message);
      await fetchDashboard();
    } finally {
      setVerifyingPatchId(null);
    }
  };

  const handleRestorePatch = async (patchId) => {
    if (!(await requestAdminConfirm("백업 복구는 현재 파일을 백업 ZIP 내용으로 되돌립니다. 계속하시겠습니까?", { expectedText: "RESTORE", inputLabel: "Type RESTORE to continue." }))) return;
    setRestoringPatchId(patchId);
    try {
      await restoreCodePatch(apiBase, token, patchId);
      await fetchDashboard();
      showAdminNotice("Backup restored. Run verification again before restart.");
    } catch (err) {
      showAdminNotice(err.message);
      await fetchDashboard();
    } finally {
      setRestoringPatchId(null);
    }
  };

  const handleRestartBackend = async () => {
    if (!(await requestAdminConfirm("백엔드 재시작 중 잠시 접속이 끊길 수 있습니다. 계속하시겠습니까?", { expectedText: "RESTART", inputLabel: "Type RESTART to continue." }))) return;
    return restartBackend(apiBase, token);
  };

  const handleSafeDeploy = async () => {
    if (!(await requestAdminConfirm("안전 배포는 검증을 먼저 실행하고, 통과한 경우에만 백엔드 재시작을 요청합니다. 계속하시겠습니까?", { expectedText: "DEPLOY", inputLabel: "Type DEPLOY to continue." }))) return;
    return runSafeDeploy(apiBase, token);
  };

  const refreshServerStatus = async () => {
    const data = await fetchServerStatus(apiBase, token);
    setServerStatus(data);
    return data;
  };

  const handleLoadDevConsoleRoots = async () => {
    setDevConsoleBusy(true);
    try {
      const data = await fetchDevConsoleRoots(apiBase, token);
      setDevConsoleRoots(data.roots || []);
      setDevConsoleNotice("서버 접속 루트를 불러왔습니다.");
      return data;
    } catch (err) {
      setDevConsoleNotice(err.message);
      return null;
    } finally {
      setDevConsoleBusy(false);
    }
  };

  const handleOpenDevConsoleDir = async (path = devConsoleCwd) => {
    setDevConsoleBusy(true);
    try {
      const data = await listDevConsoleDir(apiBase, token, path || "/home/tobe-project");
      setDevConsoleCwd(data.path || path);
      setDevConsoleParent(data.parent || "");
      setDevConsoleItems(data.items || []);
      setDevConsoleNotice("");
      return data;
    } catch (err) {
      setDevConsoleNotice(err.message);
      return null;
    } finally {
      setDevConsoleBusy(false);
    }
  };

  const handleOpenDevConsoleFile = async (path) => {
    setDevConsoleBusy(true);
    try {
      const data = await readDevConsoleFile(apiBase, token, path);
      setDevConsoleFile(data);
      setDevConsoleContent(data.content || "");
      setDevConsoleNotice(`${data.name || "파일"}을 열었습니다.`);
    } catch (err) {
      setDevConsoleNotice(err.message);
    } finally {
      setDevConsoleBusy(false);
    }
  };

  const handleSaveDevConsoleFile = async () => {
    if (!devConsoleFile?.path) return;
    if (!(await requestAdminConfirm(`${devConsoleFile.path} 파일을 서버에 저장합니다. 계속하려면 SAVE를 입력하세요.`, {
      expectedText: "SAVE",
      confirmLabel: "저장",
      danger: true,
    }))) return;
    setDevConsoleBusy(true);
    try {
      await writeDevConsoleFile(apiBase, token, devConsoleFile.path, devConsoleContent);
      setDevConsoleNotice("서버 파일을 저장했습니다.");
      showAdminNotice("서버 파일을 저장했습니다.");
      await handleOpenDevConsoleDir(devConsoleCwd);
    } catch (err) {
      setDevConsoleNotice(err.message);
    } finally {
      setDevConsoleBusy(false);
    }
  };

  const handleRunDevConsoleCommand = async () => {
    const command = devConsoleCommand.trim();
    if (!command) return;
    if (!(await requestAdminConfirm(`${devConsoleCwd} 경로에서 명령어를 실행합니다. 계속하려면 RUN을 입력하세요.`, {
      expectedText: "RUN",
      confirmLabel: "실행",
      danger: true,
      preview: command,
    }))) return;
    setDevConsoleBusy(true);
    try {
      const data = await runDevConsoleCommand(apiBase, token, devConsoleCwd, command);
      setDevConsoleOutput(`$ ${data.command}\n[exit ${data.returncode}]\n${data.output || ""}`);
      setDevConsoleNotice(data.status === "success" ? "명령 실행이 완료되었습니다." : "명령 실행 결과를 확인하세요.");
    } catch (err) {
      setDevConsoleOutput(`$ ${command}\n${err.message}`);
      setDevConsoleNotice(err.message);
    } finally {
      setDevConsoleBusy(false);
    }
  };

  useEffect(() => {
    if (adminPage !== "devConsole") return;
    if (!devConsoleRoots.length) handleLoadDevConsoleRoots();
    if (!devConsoleItems.length) handleOpenDevConsoleDir(devConsoleCwd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminPage]);

  const menuItems = [
    { id: "overview", label: "전체 현황", description: "시스템 상태" },
    { id: "server", label: "서버 모니터링", description: "상태/도메인/인증서" },
    { id: "users", label: "회원 관리", description: "승인 및 권한" },
    { id: "companyAi", label: "회사 AI 생성", description: "브랜드/템플릿/AI" },
    { id: "clientSync", label: "클라이언트 동기화", description: "PC/모바일 학습" },
    { id: "knowledge", label: "지식 관리", description: "추가 및 삭제" },
    { id: "learned", label: "학습 지식 검토", description: "웹 자동학습 승인" },
    { id: "search", label: "검색 관리", description: "로컬/API 설정" },
    { id: "weather", label: "날씨 API", description: "캐시 및 공급자" },
    { id: "externalApi", label: "외부 API 관리", description: "KHOA·KMA·PG·TOBE_AI 등" },
    { id: "logs", label: "감사 로그", description: "관리 기록" },
  // ★2026-09-18: 협력사 관리자(company_owner/company_admin)에겐 자기 회사에서 실제로 쓰는 메뉴만 보인다.
  //   그 전엔 서버 모니터링·지식 관리 등 본사 전용 메뉴가 전부 보여 눌러도 빈 화면·403 오류만 났다.
  ].filter((item) => isSuperAdmin || ["overview", "users"].includes(item.id));

  return (
    <section className="admin-page">
      <AdminSummary isSuperAdmin={isSuperAdmin} loading={loading} onRefresh={fetchDashboard} />

      {error && <div className="admin-error">{error}</div>}
      {adminNotice && (
        <div className="admin-inline-notice" role="status">
          <span>{adminNotice}</span>
          <button type="button" onClick={() => setAdminNotice("")} aria-label="관리자 알림 닫기">×</button>
        </div>
      )}
      {adminDialog && (
        <div className="admin-inline-dialog" role="dialog" aria-modal="true">
          <strong>관리자 확인</strong>
          <p>{adminDialog.message}</p>
          {(adminDialog.inputMode === "text" || adminDialog.expectedText) && (
            <label>
              {adminDialog.inputLabel || "입력"}
              <input
                value={adminDialog.inputValue || ""}
                onChange={(event) => setAdminDialog((prev) => ({ ...prev, inputValue: event.target.value }))}
                placeholder={adminDialog.expectedText || ""}
              />
            </label>
          )}
          <div>
            <button type="button" className={adminDialog.expectedText ? "admin-danger-button" : "admin-approve-button"} onClick={confirmAdminDialog}>
              {adminDialog.confirmLabel || "확인"}
            </button>
            <button type="button" className="admin-approve-button" onClick={() => closeAdminDialog(null)}>취소</button>
          </div>
        </div>
      )}

      <div className="admin-layout">
        <nav className="admin-page-menu" aria-label="Admin menu">
          {menuItems.map((item) => (
            <button key={item.id} className={adminPage === item.id ? "active" : ""} onClick={() => setAdminPage(item.id)}>
              <strong>{item.label}</strong>
              <span>{item.description}</span>
            </button>
          ))}
        </nav>

        <div className="admin-page-content">
          {adminPage === "overview" && (
            <>
            <DashboardCards overview={overview} users={users} isSuperAdmin={isSuperAdmin} agentTasks={agentTasks} agentStatus={agentStatus} />
              <DashboardPanels section="overview" overview={overview} auditLogs={auditLogs} agentTasks={agentTasks} agentError={agentError} isSuperAdmin={isSuperAdmin} onAgentApprove={handleAgentApprove} approvingTaskIds={approvingTaskIds} />
            </>
          )}

          {adminPage === "clientSync" && (
            <ClientSyncPanel apiBase={apiBase} token={token} />
          )}

          {adminPage === "companyAi" && (
            <TenantOnboardingPanel apiBase={apiBase} token={token} />
          )}

          {adminPage === "users" && (
            <UserManagementTable
              loading={loading}
              users={users}
              currentUser={currentUser}
              isSuperAdmin={isSuperAdmin}
              canCreateUsers={canCreateCompanyUsers}
              visibleRoles={visibleRoles}
              onCreateUser={handleCreateUser}
              onApprove={handleApprove}
              onRoleChange={handleRoleChange}
              onCompanyChange={handleCompanyChange}
              onContactChange={handleContactChange}
              onVerifyContact={handleVerifyContact}
              onPasswordReset={handlePasswordReset}
              onDeleteUser={handleDeleteUser}
              onHold={(userid, options = {}) => updateUser(userid, { is_approved: false }, "승인이 해제되었습니다.", options)}
            />
          )}

          {adminPage === "agent" && (
            <DashboardPanels section="agent" overview={overview} auditLogs={auditLogs} agentTasks={agentTasks} agentError={agentError} isSuperAdmin={isSuperAdmin} onAgentApprove={handleAgentApprove} approvingTaskIds={approvingTaskIds} />
          )}

          {adminPage === "codex" && (
            <section className="tobe-codex-workbench">
              <div className="tobe-codex-hero">
                <div>
                  <p className="panel-kicker">TOBE CODEX</p>
                  <h3>AI 코드 작업실</h3>
                  <p>
                    작업 지시를 입력하면 관련 파일 탐색, 작업 계획, 코드 수정 제안, 패치 생성,
                    사전검증, 백업, 적용, 복구까지 한 화면에서 진행합니다.
                  </p>
                </div>
                <div className="tobe-codex-status">
                  <span>Project map</span>
                  <strong>{codexProjectMap?.file_count ?? 0} files</strong>
                  <small>{codePatches.filter((item) => item.is_unified_diff).length} executable patches</small>
                </div>
              </div>
              <DocumentTaskPanel
                onRun={handleDocumentTaskRun}
                onCreateAppBlueprint={handleCreateCodexAppBlueprint}
                onAnalyzeSourceZip={handleAnalyzeCodexSourceZip}
                onAnalyzeReferenceImage={handleAnalyzeCodexReferenceImage}
                onAnalyzePlanningDocument={handleAnalyzeCodexPlanningDocument}
                onCreateTaskPlan={handleCreateCodexTaskPlan}
                onCreateProposal={handleCreateProposal}
                onDecideProposal={handleDecideProposal}
                onDeleteProposal={handleDeleteProposal}
                onCreatePatch={handleCreatePatch}
                onDeletePatch={handleDeletePatch}
                onRegeneratePatch={handleRegeneratePatch}
                onDryRunPatch={handleDryRunPatch}
                onPreparePatch={handlePreparePatch}
                onApplyPatch={handleApplyPatch}
                onVerifyPatch={handleVerifyPatch}
                onRestorePatch={handleRestorePatch}
                running={runningDocumentTask}
                blueprinting={creatingBlueprint}
                analyzingSourceZip={analyzingSourceZip}
                analyzingReferenceImage={analyzingReferenceImage}
                analyzingPlanningDocument={analyzingPlanningDocument}
                proposing={creatingProposal}
                patchingId={patchingProposalId}
                dryRunningPatchId={dryRunningPatchId}
                preparingPatchId={preparingPatchId}
                applyingPatchId={applyingPatchId}
                verifyingPatchId={verifyingPatchId}
                restoringPatchId={restoringPatchId}
                proposals={codeProposals}
                patches={codePatches}
                projectMap={codexProjectMap}
                appBlueprint={codexAppBlueprint}
                sourceMap={codexSourceMap}
                imageBrief={codexImageBrief}
                planningBrief={codexPlanningBrief}
                taskPlan={codexTaskPlan}
              />
            </section>
          )}

          {adminPage === "deploy" && (
            <DeploymentPanel
              onValidateZip={(file) => validateDeployZip(apiBase, token, file)}
              onUpload={(file, validationId) => uploadDeployZip(apiBase, token, file, validationId)}
              onCheck={() => runDeployCheck(apiBase, token)}
              onBuild={() => runFrontendBuild(apiBase, token)}
              onRestart={handleRestartBackend}
              onSafeDeploy={handleSafeDeploy}
              onActionComplete={fetchDashboard}
              deployRuns={deployRuns}
            />
          )}

          {adminPage === "server" && (
            <ServerManagementPanel
              apiBase={apiBase}
              token={token}
              serverStatus={serverStatus}
              onRefresh={refreshServerStatus}
              onAction={(action) => runServerAction(apiBase, token, action)}
              onFetchOpsServers={() => fetchOpsServers(apiBase, token)}
              onSaveOpsServer={(payload) => saveOpsServer(apiBase, token, payload)}
              onOpsHealth={(targetId, serverKey) => runOpsHealth(apiBase, token, targetId, serverKey)}
              onOpsExec={(targetId, command) => runOpsExec(apiBase, token, targetId, command)}
              onFetchOpsServiceMap={() => fetchOpsServiceMap(apiBase, token)}
              onOpsInventory={(targetId, serverKey) => runOpsInventory(apiBase, token, targetId, serverKey)}
              onFetchOpsHealthHistory={(targetKey) => fetchOpsHealthHistory(apiBase, token, targetKey)}
            />
          )}

          {adminPage === "devConsole" && (
            <DevConsolePanel
              roots={devConsoleRoots}
              cwd={devConsoleCwd}
              parentPath={devConsoleParent}
              items={devConsoleItems}
              selectedFile={devConsoleFile}
              fileContent={devConsoleContent}
              command={devConsoleCommand}
              commandOutput={devConsoleOutput}
              busy={devConsoleBusy}
              notice={devConsoleNotice}
              onLoadRoots={handleLoadDevConsoleRoots}
              onOpenDir={handleOpenDevConsoleDir}
              onOpenFile={handleOpenDevConsoleFile}
              onContentChange={setDevConsoleContent}
              onSaveFile={handleSaveDevConsoleFile}
              onCommandChange={setDevConsoleCommand}
              onRunCommand={handleRunDevConsoleCommand}
            />
          )}

          {adminPage === "knowledge" && (
            <KnowledgeBasePanel
              onLoad={(params) => fetchKnowledgeItems(apiBase, token, params)}
              onAddText={(payload) => addKnowledgeText(apiBase, token, payload)}
              onAddFile={(file, mode, visibility) => addKnowledgeFile(apiBase, token, file, mode, visibility)}
              onDelete={(itemId, mode) => deleteKnowledgeItem(apiBase, token, itemId, mode)}
              onChanged={fetchDashboard}
            />
          )}

          {adminPage === "learned" && (
            <LearnedReviewPanel
              onLoad={(params) => fetchLearnedPending(apiBase, token, params)}
              onSetStatus={(itemId, status) => setLearnedStatus(apiBase, token, itemId, status)}
              onChanged={fetchDashboard}
            />
          )}


          {adminPage === "search" && (
            <SearchSettingsPanel
              settings={searchSettings}
              onLoad={fetchSearchSettings}
              onSave={(payload) => updateSearchSettings(apiBase, token, payload)}
              onTest={(payload) => testSearchSettings(apiBase, token, payload)}
            />
          )}

          {adminPage === "weather" && (
            <WeatherSettingsPanel
              settings={weatherSettings}
              onLoad={fetchWeatherSettings}
              onSave={(payload) => updateWeatherSettings(apiBase, token, payload)}
              onTest={(payload) => testWeatherSettings(apiBase, token, payload)}
            />
          )}

                    {adminPage === "externalApi" && (
            <ExternalApiPanel apiBase={apiBase} token={token} />
          )}
          {adminPage === "logs" && (
            <DashboardPanels section="logs" overview={overview} auditLogs={auditLogs} agentTasks={agentTasks} agentError={agentError} isSuperAdmin={isSuperAdmin} onAgentApprove={handleAgentApprove} approvingTaskIds={approvingTaskIds} />
          )}
        </div>
      </div>
    </section>
  );
}

export default AdminDashboard;
